import { getVisitUSState } from "~/visits/utils/visitCookies";

import { isStateControlledMedication } from "~/shared/utils/sharedUtils";
import { isPDMPSleepMedication } from "../assets/js/bundles/shared/utils/sharedUtils";

const CONTROLLED_MED_NOTICE = `<div class="controlled-medication-disclaimer">Cannot be prescribed online because it is a controlled medication in your state</div>`;
const PDMP_MED_DISCLAIMER = `<div class="controlled-medication-disclaimer">Cannot be prescribed online in your state as it is a monitored prescription medication</div>`;

// So the first set of questions above looks good as a general screen.
// The second set, is more tailored for propranolol and cardiac / respiratory issues. I would add a "Do you have any history of cardiac issues," to the top list.
// For the bottom list

// 0. Yes to screen for
// PHQ2, GAD2
// OSA,
// EDS,
// 1. Bipolar or mental health conditions
// 2. Alcohol or substance abuse
// 3. history of car accidents, or mistakes due to excessive daytime or morning sleepiness
// 4. Nasal congestion or mouth breathing
// 5. Parasomnias - or strange behaviors at night - sleep walking, sleep talking, bedwetting, etc
// 6. Morning headaches, palpitations, or waking at night (another sleep apnea screen)
// 7. A history of dependence on anxiety or sleep medications

// Do we have a STOP BANG, PHQ9, and Eppworth Sleepiness Scale in there already?

export default function () {
  return {
    name: "Insomnia Screening",
    description:
      "We can help you find out if medication might help with your insomnia",
    version: "2024041401",
    service_code: "INSOMNIA",
    live: false,
    staged: true,
    questions: [
      {
        title: "Why are you interested in treatment for insomnia?",
        code: "why-interested",
        description:
          "This helps us understand and recommend the right treatment for you.",
        type: "checkbox",
        choices: [
          {
            label: "Have tried other treatment options without success",
          },
          {
            label: "Looking for a medication to treat your insomnia symptoms",
          },
          {
            label: "Heard about medications from someone around you",
          },
          {
            label: "Heard about medications in the news",
          },
          {
            label: "I've used Kick in the past",
          },
          {
            label: "Research on the internet",
          },
          {
            label: "Other",
            children: [
              {
                title: "Please tell us why you're interested...",
                type: "text",
                code: "why-interested-other",
              },
            ],
          },
        ],
      },

      {
        title:
          "Which of the following sleep difficulties are you experiencing?",
        code: "insomnia-disorder-screening",
        description:
          "Certain conditions can complicate diagnosis, increase risks, or change the recommended treatments so it's critical we know everything going on with your health.",
        type: "checkbox",
        choices: [
          {
            label: "Difficulty falling asleep / initiating sleep",
          },
          {
            label: "Difficulty staying asleep / maintaining sleep",
          },
          {
            label: "Early morning awakening",
          },
          {
            label:
              "Sleep issues are causing work, school, social or behavioral distress",
          },
          {
            label: "Sleep issues occur at least 3 nights per week or more",
          },
          {
            label: "Unable to sleep even if the setting and time allow it",
          },
          {
            label: "None of the above",
            silent: true,
          },
        ],
      },

      {
        title:
          "What have you tried to help with your sleep difficulties? (Check all that apply)",
        code: "insomnia-other-treatments-tried",
        description:
          "Please be as detailed as possible - this helps us understand what you've already tried and customize a treatment plan personalized to you.",
        type: "checkbox",
        choices: [
          {
            label: "Melatonin",
          },
          {
            label:
              "Over the counter sleep aids (e.g. Benadryl, Unisom, Tylenol PM, etc.)",
            children: [
              {
                title: "Tell us more about the over the counter sleep aids",
                description:
                  "Tell us more about the over the counter sleep aids",
                code: "insomnia-other-treatments-tried-sleep-aids",
                type: "textarea",
              },
            ],
          },
          {
            label:
              "Supplements or vitamins (e.g. Magnesium, GABA, Ashwagandha, Valerian root, etc.)",
            children: [
              {
                title:
                  "Which supplements or vitamins have you tried? Are you currently taking any?",
                description:
                  "Which supplements or vitamins have you tried? Are you currently taking any?",
                code: "insomnia-other-treatments-tried-supplements",
                type: "textarea",
              },
            ],
          },
          {
            label:
              "Sleep Hygiene (e.g. no screens before bed, no caffeine, etc.)",
            children: [
              {
                title: "Tell us more...",
                description: "Tell us more...",
                code: "insomnia-other-treatments-tried-sleep-hygiene",
                type: "textarea",
              },
            ],
          },
          {
            label: "Sleep gadgets (e.g. Fitbit, Oura, Whoop, etc.)",
          },
          {
            label:
              "Mobile Apps (e.g. Calm, Headspace, Sleep Cycle, SleepScore, etc.)",
            children: [
              {
                title: "Which ones? How'd it go?",
                description: "Which ones? How'd it go?",
                code: "insomnia-other-treatments-tried-mobile-apps",
                type: "textarea",
              },
            ],
          },
          {
            label:
              "Meditation, relaxation, or other behavioral techniques (e.g. CBT-I)",
            children: [
              {
                title: "Tell us more...",
                description: "Tell us more...",
                code: "insomnia-other-treatments-tried-behavioral-techniques",
                type: "textarea",
              },
            ],
          },
          {
            label: "Other",
            children: [
              {
                title: "Tell us more...",
                description: "Tell us more...",
                code: "insomnia-other-treatments-tried-other",
                type: "textarea",
              },
            ],
          },
          {
            label: "None of the above",
            silent: true,
          },
        ],
      },
      {
        title: "What prescription sleep medications have you used?",
        code: "insomnia-prescripton-medications-tried",
        description:
          "Let us know the medications you have used before so your doctor can personalize your sleep treatment plan.",
        type: "checkbox",
        choices: [
          { label: "Ambien / Zolpidem" },
          {
            label: "Cyclobenzaprine",
            children: [
              {
                title: "Did Cyclobenzaprine work? What dosage?",
                type: "textarea",
                code: "insomnia-prescripton-medications-tried-cyclobenzaprine-detail",
              },
            ],
          },
          { label: "Dayvigo" },
          {
            label: "Gabapentin",
            children: [
              {
                title: "Did Gabapentin work? What dosage?",
                type: "textarea",
                code: "insomnia-prescripton-medications-tried-gabapentin-detail",
              },
            ],
          },
          {
            label: "Guanfacine",
            children: [
              {
                title: "Did Guanfacine work? What dosage?",
                type: "textarea",
                code: "insomnia-prescripton-medications-tried-guanfacine-detail",
              },
            ],
          },
          {
            label: "Hydroxyzine",
            children: [
              {
                title: "Did Hydroxyzine work? What dosage?",
                type: "textarea",
                code: "insomnia-prescripton-medications-tried-hydroxyzine-detail",
              },
            ],
          },
          { label: "Lunesta / Eszopiclone" },
          {
            label: "Quetiapine / Seroquel",
            children: [
              {
                title: "Did Quetiapine work?",
                type: "textarea",
                code: "insomnia-prescripton-medications-tried-quetiapine-detail",
              },
            ],
          },
          {
            label: "Ramelteon / Rozerem",
            children: [
              {
                title: "Did Ramelteon work?",
                type: "textarea",
                code: "insomnia-prescripton-medications-tried-ramelteon-detail",
              },
            ],
          },
          { label: "Restoril / Temazepam" },
          { label: "Silenor / Doxepin" },
          { label: "Sonata / Zaleplon" },
          {
            label: "Trazodone",
            children: [
              {
                title: "Did Trazodone work? What dosage?",
                type: "textarea",
                code: "insomnia-prescripton-medications-tried-trazodone-detail",
              },
            ],
          },
          {
            label: "Other",
            children: [
              {
                title:
                  "Which prescription medications and dosage did you try? Did it work?",
                code: "insomnia-prescripton-medications-tried-other",
                type: "textarea",
              },
            ],
          },
          {
            label: "None of the above",
            silent: true,
          },
        ],
      },
      {
        title:
          "Are there any sleep medications you are interested in learning more about?",
        code: "medications-interested-in-checkbox",
        description:
          "Please note we do not prescribe controlled medications such as Ambien or Lunesta.",
        type: "checkbox",
        choices: [
          {
            label: `Cyclobenzaprine${
              isStateControlledMedication(getVisitUSState(), "cyclobenzaprine")
                ? CONTROLLED_MED_NOTICE
                : isPDMPSleepMedication(getVisitUSState(), "cyclobenzaprine")
                ? PDMP_MED_DISCLAIMER
                : ""
            }`,
            disabled:
              isStateControlledMedication(
                getVisitUSState(),
                "cyclobenzaprine"
              ) || isPDMPSleepMedication(getVisitUSState(), "cyclobenzaprine"),
          },
          {
            label: `Gabapentin${
              isStateControlledMedication(getVisitUSState(), "gabapentin")
                ? CONTROLLED_MED_NOTICE
                : isPDMPSleepMedication(getVisitUSState(), "gabapentin")
                ? PDMP_MED_DISCLAIMER
                : ""
            }`,
            children: [
              {
                title: "Could you tell us why you're interested in Gabapentin?",
                code: "insomnia-medications-interested-gabapentin-detail",
                type: "textarea",
              },
            ],
            disabled:
              isStateControlledMedication(getVisitUSState(), "gabapentin") ||
              isPDMPSleepMedication(getVisitUSState(), "gabapentin"),
          },
          { label: "Guanfacine" },
          { label: "Hydroxyzine" },
          { label: "Quetiapine" },
          { label: "Ramelteon" },
          { label: "Trazodone" },
          {
            label: "No - Let my doctor guide me",
            silent: true,
          },
        ],
      },
      {
        title: "Do you have any medical conditions?",
        code: "medical-conditions",
        description:
          "Include even any minor or recent conditions or symptoms. To give you the best care we need to know your full history.",
        type: "radio",
        choices: [
          {
            label: "Yes",
            children: [
              {
                title: "What medical conditions do you have?",
                description:
                  "Even include things you think you have whether or not you've been diagnosed by your doctor. We will message or call you if we need more information.",
                code: "medical-conditions",
                type: "textarea",
              },
            ],
          },
          {
            label: "No, don't have any medical conditions",
            silent: true,
          },
        ],
      },
      {
        title: "Have you ever had any surgeries or hospitalizations?",
        code: "surgeries-hospitalizations",
        description:
          "Include any significant emergency room visits, hospital stays, or surgeries, including cosmetic surgeries.",
        type: "radio",
        choices: [
          {
            label: "Yes",
            children: [
              {
                title: "What surgeries or hospitalizations have you had?",
                description:
                  "List all the procedures and events and try to remember when they occurred.",
                code: "hospitalizations",
                type: "textarea",
              },
            ],
          },
          {
            label: "No, never had surgery or been hospitalized",
            silent: true,
          },
        ],
      },
      {
        title: "Do you currently take any medicines, herbals, or supplements?",
        code: "medicines-herbals-supplements",
        description:
          "Please include any medicines you finished recently, including topical medicines, and any injections, vitamins, herbal remedies, or any other products you use.",
        type: "radio",
        choices: [
          {
            label: "Yes",
            children: [
              {
                title: "What medicines, herbals, or supplements do you use?",
                description:
                  "Please list the dose and why you are taking each medicine or supplement.",
                code: "medications",
                type: "textarea", // @todo multi-select autocomplete
              },
            ],
          },
          {
            label:
              "No, not taking any medicines, supplements, herbals, or other prescriptions",
            silent: true,
          },
        ],
      },
      {
        title: "Do you have any allergies or medication reactions?",
        code: "allergies-medication-reactions",
        description:
          "Please include any allergies or major reactions to medicines.",
        type: "radio",
        choices: [
          {
            label: "Yes",
            children: [
              {
                title: "What allergies do you have?",
                description:
                  "List the allergies and if possible, the severity of any reaction you have had.",
                code: "allergies",
                type: "textarea", // @todo multi-select autocomplete
              },
            ],
          },
          {
            label: "No, don't have any allergies or medication reactions",
            silent: true,
          },
        ],
      },

      {
        title:
          "Tell us in your own words what sleep problems you are experiencing and how you feel?",
        description:
          "Please provide as much information as possible so your doctor can perform a thorough evaluation.",
        type: "textarea",
        code: "chief-complaint",
      },
      {
        title: "Do you consider yourself a morning person or a night person?",
        code: "sleep-chronotype",
        description: "",
        type: "radio",
        choices: [
          { label: "Morning person" },
          { label: "Night person" },
          { label: "Not sure" },
        ],
      },
      {
        title: "What's your ideal bedtime?",
        description: "",
        type: "radio",
        code: "sleep-bed-time-ideal",
        choices: [
          {
            label: "Earlier than 7pm",
          },
          {
            label: "7pm",
          },
          {
            label: "8pm",
          },
          {
            label: "9pm",
          },
          {
            label: "10pm",
          },
          {
            label: "11pm",
          },
          {
            label: "12am",
          },
          {
            label: "1am",
          },
          {
            label: "2am",
          },
          {
            label: "3am",
          },
          {
            label: "4am",
          },
          {
            label: "Later than 4am",
          },
        ],
      },
      {
        title: "What time do you find yourself actually going to bed?",
        description:
          "If different from your ideal bedtime, what time do you find yourself getting to bed?",
        type: "radio",
        code: "sleep-bed-time-actual",
        choices: [
          {
            label: "earlier than 7pm",
          },
          {
            label: "7pm",
          },
          {
            label: "8pm",
          },
          {
            label: "9pm",
          },
          {
            label: "10pm",
          },
          {
            label: "11pm",
          },
          {
            label: "12am",
          },
          {
            label: "1am",
          },
          {
            label: "2am",
          },
          {
            label: "3am",
          },
          {
            label: "4am",
          },
          {
            label: "later than 4am",
          },
        ],
      },
      {
        title: "What's your ideal wake up time?",
        description: "",
        type: "radio",
        code: "sleep-wake-time-ideal",
        choices: [
          {
            label: "Earlier than 4am",
          },
          {
            label: "4am",
          },
          {
            label: "5am",
          },
          {
            label: "6am",
          },
          {
            label: "7am",
          },
          {
            label: "8am",
          },
          {
            label: "9am",
          },
          {
            label: "10am",
          },
          {
            label: "11am",
          },
          {
            label: "12pm",
          },
          {
            label: "1pm",
          },
          {
            label: "2pm",
          },
          {
            label: "Later than 2pm",
          },
        ],
      },
      {
        title:
          "What time do you normally wake up on weekdays? (Monday - Friday) ",
        description: "",
        type: "radio",
        code: "sleep-wake-time-weekday",
        choices: [
          {
            label: "Earlier than 4am",
          },
          {
            label: "4am",
          },
          {
            label: "5am",
          },
          {
            label: "6am",
          },
          {
            label: "7am",
          },
          {
            label: "8am",
          },
          {
            label: "9am",
          },
          {
            label: "10am",
          },
          {
            label: "11am",
          },
          {
            label: "12pm",
          },
          {
            label: "1pm",
          },
          {
            label: "2pm",
          },
          {
            label: "Later than 2pm",
          },
        ],
      },
      {
        title: "What time do you wake up on weekends (Saturday / Sunday)?",
        description: "",
        type: "radio",
        code: "sleep-wake-time-weekends",
        choices: [
          {
            label: "Earlier than 4am",
          },
          {
            label: "4am",
          },
          {
            label: "5am",
          },
          {
            label: "6am",
          },
          {
            label: "7am",
          },
          {
            label: "8am",
          },
          {
            label: "9am",
          },
          {
            label: "10am",
          },
          {
            label: "11am",
          },
          {
            label: "12pm",
          },
          {
            label: "1pm",
          },
          {
            label: "2pm",
          },
          {
            label: "Later than 2pm",
          },
        ],
      },
      {
        title: "How long does it take you to fall asleep once in bed?",
        description: "",
        type: "radio",
        code: "sleep-fall-asleep-time",
        choices: [
          {
            label: "Less than 5 mins",
          },
          {
            label: "15 minutes",
          },
          {
            label: "30 minutes",
          },
          {
            label: "1 hour",
          },
          {
            label: "1.5 hours",
          },
          {
            label: "2 hours",
          },
          {
            label: "More than 2 hours",
          },
        ],
      },
      {
        title: "Do any of the following bedtime habits apply to you?",
        description: "This helps us understand your current bedtime routines",
        type: "checkbox",
        choices: [
          {
            label: "Use a computer an hour or less before going to bed",
          },
          {
            label: "Use your phone right before bed",
          },
          {
            label:
              "Have bright light exposure in your bedroom or home before bed",
          },
          {
            label: "Drink coffee or use caffeine after 3pm",
          },
          {
            label: "Drink alcohol at night or before bed",
          },
          {
            label: "Exercise in the evening",
          },
          {
            label: "None of the above",
            silent: true,
          },
        ],
      },

      {
        title:
          "On average, how many times a night do you wake up in the middle of the night?",
        code: "sleep-wake-ups-per-night",
        description:
          "This helps us understand your current sleep quality and offer suggestions to improve it",
        type: "radio",
        choices: [
          { label: "0" },
          { label: "1-2" },
          { label: "3-4" },
          { label: "5 or more" },
        ],
      },

      {
        title:
          "If you wake up in the middle of the night, How soon after you fall asleep are you waking up?",
        code: "sleep-wake-ups-how-soon-after-falling-asleep",
        description:
          "This helps us understand your current sleep quality and offer suggestions to improve it",
        type: "checkbox",
        choices: [
          { label: "1-2 hours" },
          { label: "2-4 hours" },
          { label: "4-6 hours" },
          { label: "6-8 hours" },
          { label: "Not applicable", silent: true },
        ],
      },

      {
        title:
          "If you wake up in the middle of the night, how long does it take you to fall back asleep?",
        code: "sleep-wake-ups-time-to-fall-back-asleep",
        description: "",
        type: "radio",
        choices: [
          { label: "5 minutes or less" },
          { label: "15 minutes" },
          { label: "30 minutes" },
          { label: "An hour" },
          { label: "More than an hour" },
          { label: "Not applicable", silent: true },
        ],
      },

      {
        title: "Do you have any of the following conditions?",
        description:
          "Even if they were in the past, certain conditions can complicate diagnosis, increase risks, or change the recommended treatments so it's critical we know your full history.",
        type: "checkbox",
        code: "conditions",
        choices: [
          {
            label:
              "Any problems with poor circulation, or cold or blue extremities?",
          },
          {
            label: "Are you pregnant?",
          },
          {
            label: "Are you breastfeeding?",
          },
          {
            label: "Are you menopausal?",
          },
          {
            label: "Do you take any medication to lower blood pressure?",
            children: [
              {
                title:
                  "Please list your blood pressure medications and dosages if you know them.",
                description:
                  "list your blood pressure medications and dosages if you know them",
                code: "blood-pressure-medication-details",
                type: "textarea",
              },
            ],
          },
          {
            label: "Any liver problems?",
          },
          {
            label: "Any kidney problems?",
          },
          {
            label: "Any history of cardiac/heart issues?",
            children: [
              {
                title: "Tell us more about the heart condition / problem...",
                description: "Tell us more about the heart condition / problem",
                code: "history-heart-condition-detail",
                type: "textarea",
              },
            ],
          },
          {
            label:
              "Are you currently experiencing any symptoms of sexual dysfunction?",
          },
          {
            label: "Do you have glaucoma?",
          },
          {
            label:
              "Ever been diagnosed with any mental disorders such as bipolar disorder or schizophrenia?",
            children: [
              {
                title: "What mental disorders have you been diagnosed with?",
                description:
                  "List the disordera and if possible, when you were diagnosed.",
                code: "mental-disorders",
                type: "textarea",
              },
            ],
          },
          {
            label:
              "Ever been diagnosed with any neurological disorder such as Parkinson’s disease or dementia?",
            children: [
              {
                title:
                  "What neurological disorders have you been diagnosed with?",
                description:
                  "List the disordera and if possible, when you were diagnosed.",
                code: "neurological-disorders",
                type: "textarea",
              },
            ],
          },
          {
            label: "None of the above",
            silent: true,
          },
        ],
      },
      {
        title:
          "Have you ever had any of the following sleep-related conditions?",
        description:
          "Even if they were in the past, certain conditions can complicate diagnosis, increase risks, or change the recommended treatments so it's critical we know your full history.",
        type: "checkbox",
        code: "sleep-rule-outs",
        choices: [
          { label: "Any family history of narcolepsy?" },
          {
            label:
              "Any recurring episodes, more than 3 times a week over the last 3 months, of an irresistible urge to sleep?",
          },
          {
            label:
              "Episodes of sudden muscle weakness, precipitated by laughter or joking?",
            children: [
              {
                title: "Tell us more...",
                type: "text",
                code: "cataplexy-details",
              },
            ],
          },
          {
            label:
              "Any history of car accidents or mistakes due to excessive daytime or morning sleepiness?",
            children: [
              {
                title: "Tell us what happened...",
                type: "text",
                code: "car-accident-details",
              },
            ],
          },
          {
            label: "Any history of nasal congestion or mouth breathing?",
            children: [
              {
                title: "Tell us more...",
                type: "text",
                code: "mouth-breathing-details",
              },
            ],
          },
          {
            label:
              "Any morning headaches, heart palpitations, or waking up suddenly at night?",
            children: [
              {
                title: "Tell us more...",
                type: "text",
                code: "sleep-apnea-morning-headache-screen-details",
              },
            ],
          },
          {
            label: "Any history of dependence on anxiety or sleep medications?",
            children: [
              {
                title: "Tell us more...",
                type: "text",
                code: "anxiety-sleep-medication-dependence-details",
              },
            ],
          },
          {
            label: "None of the above",
            silent: true,
          },
        ],
      },
      {
        title:
          "Have you ever had any of the following strange behaviors in your sleep?",
        description:
          "Even if they were in the past, certain conditions can complicate diagnosis, increase risks, or change the recommended treatments so it's critical we know your full history.",
        type: "checkbox",
        code: "parasomnia-rule-outs",
        choices: [
          {
            label:
              "Any history of strange behaviors at night such as sleepwalking, sleep talking, eating in your sleep, or bedwetting?",
            children: [
              {
                title: "What strange behaviors have you experienced?",
                type: "text",
                code: "parasomnia-behavior-details",
              },
            ],
          },
          {
            label:
              "Any episodes of acting out dreams? Kicking or thrashing while asleep?",
            children: [
              {
                title: "What have you experienced?",
                type: "text",
                code: "parasomnia-acting-out-details",
              },
            ],
          },
          {
            label: "None of the above",
            silent: true,
          },
        ],
      },
      {
        title:
          "Have you ever had any of the following snoring or sleep-apnea related conditions?",
        description:
          "Even if they were in the past, certain conditions can complicate diagnosis, increase risks, or change the recommended treatments so it's critical we know your full history.",
        type: "checkbox",
        code: "stop-bang",
        choices: [
          {
            label: "Do you snore loudly?",
            children: [
              {
                title:
                  "Tell us more - how often do you snore, and how loud is it? Any treatment?",
                description:
                  "How often do you snore, and how loud is it? Any treatment?",
                code: "history-stopbang-snoring-detail",
                type: "textarea",
              },
            ],
          },
          {
            label: "Do you often feel tired or sleepy during the day?",
          },
          {
            label:
              "Has anyone ever observed you stop breathing or gasping at night?",
            children: [
              {
                title: "Tell us more - be as detailed as possible.",
                description: "Tell us more - be as detailed as possible.",
                code: "history-stopbang-stop-breathing-detail",
                type: "textarea",
              },
            ],
          },
          {
            label:
              "Do you have or are you being treated for high blood pressure?",
          },
          {
            label:
              "Is your waist size over 40 inches if you're male, or 35 inches if you're female?",
            children: [
              {
                title: "What's your height in feet?",
                code: "height-feet",
                type: "text",
              },
              {
                title: "What's your height in inches?",
                code: "height-inches",
                type: "text",
              },
              {
                title: "What's your weight in lbs?",
                code: "weight-lbs",
                type: "text",
              },
            ],
          },
          {
            label: "Are you older than 50?",
          },
          {
            label:
              "Is your neck circumference greater than 17 inches if you're male, or 16 inches if you're female?",
          },
          {
            label: "None of the above",
            silent: true,
          },
        ],
      },
      {
        title:
          "What is the TOP, higher systolic reading of your blood pressure taken in the last 6 months?",
        description:
          "In order to provide a safe and effective treatment plan, we need an accurate blood pressure measurement. Blood pressure results are a higher number over another lower number; for example: 115/75. Please start by entering the higher number below (you’ll enter the lower number on the next screen).",
        code: "systolic",
        type: "bloodpressure-systolic",
        choices: [
          {
            label: "160 or higher",
            rightSideColorLabel: {
              text: "Very High",
              color: "#740a09",
            },
          },
          {
            label: "140-159",
            rightSideColorLabel: {
              text: "High",
              color: "#d94442",
            },
          },
          {
            label: "120-139",
            rightSideColorLabel: {
              text: "Normal",
              color: "#45bd57",
            },
          },
          {
            label: "95-119",
            rightSideColorLabel: {
              text: "Lower",
              color: "#6ecba4",
            },
          },
          {
            label: "Under 95",
            rightSideColorLabel: {
              text: "Low",
              color: "#37a0d8",
            },
          },
        ],
      },
      {
        title:
          "What is the BOTTOM, lower diastolic reading of your blood pressure taken in the last 6 months?",
        description:
          "Please select the lower number of your blood pressure reading, it is very important to treat you safely.",
        code: "diastolic",
        type: "bloodpressure-diastolic",
        choices: [
          {
            label: "100 or over",
            rightSideColorLabel: {
              text: "Very High",
              color: "#740a09",
            },
          },
          {
            label: "90-99",
            rightSideColorLabel: {
              text: "High",
              color: "#d94442",
            },
          },
          {
            label: "80-90",
            rightSideColorLabel: {
              text: "Normal",
              color: "#45bd57",
            },
          },
          {
            label: "65-79",
            rightSideColorLabel: {
              text: "Lower",
              color: "#6ecba4",
            },
          },
          {
            label: "Under 65",
            rightSideColorLabel: {
              text: "Low",
              color: "#37a0d8",
            },
          },
        ],
      },
      {
        title:
          "Do you drink alcohol, smoke cigarettes, or use other substances?",
        description:
          "Please answer accurately. It's important we know to treat you safely",
        type: "checkbox",
        choices: [
          {
            label: "Do you use alcohol regularly?",
            children: [
              {
                title: "How many drinks a week, on average?",
                code: "alcohol-frequency",
                type: "text",
              },
            ],
          },
          {
            label: "Do you smoke or vape?",
            children: [
              {
                title: "How often do you smoke or vape a week, on average? ",
                code: "tobacco-frequency",
                type: "text",
              },
            ],
          },
          {
            label: "Any other substances?",
            children: [
              {
                title: "What substances do you use, and how frequently?",
                code: "substances-frequency",
                type: "textarea",
              },
            ],
          },
          {
            label: "None of the above",
            silent: true,
          },
        ],
      },
      {
        title: "Have you ever: ",
        type: "checkbox",
        choices: [
          {
            label:
              "Felt you need to cut down on use of a substance (alcohol, cocaine, cannabis, etc)",
          },
          {
            label: "Been annoyed by people criticizing your use of substances?",
          },
          {
            label: "Felt guilty about your use of a substance?",
          },
          {
            label:
              "Needed a drink first thing in the morning as an eye-opener?",
          },
          {
            label: "None of the above",
            silent: true,
          },
        ],
      },
      {
        title:
          "Do you have any concerns about your mood, depression, anxiety or worrying?",
        code: "PHQ-GAD-2",
        type: "radio",
        choices: [
          {
            label: "No",
            silent: true,
          },
          {
            label: "Yes",
            children: [
              {
                title:
                  "Over the past two weeks, how often have you felt little interest or pleasure in doing things?",
                code: "PHQ2-1",
                type: "radio",
                choices: [
                  {
                    label: "Not At all",
                  },
                  {
                    label: "Several Days",
                  },
                  {
                    label: "More than Half the Days",
                  },
                  {
                    label: "Nearly Every Day",
                  },
                ],
              },
              {
                title:
                  "Over the past two weeks, how often have you felt down, depressed or hopeless?",
                type: "radio",
                code: "PHQ2-2",
                choices: [
                  {
                    label: "Not At all",
                  },
                  {
                    label: "Several Days",
                  },
                  {
                    label: "More than Half the Days",
                  },
                  {
                    label: "Nearly Every Day",
                  },
                ],
              },
              {
                title:
                  "Over the past two weeks, how often have you felt nervous, anxious, or on edge?",
                type: "radio",
                code: "GAD2-1",
                choices: [
                  {
                    label: "Not At all",
                  },
                  {
                    label: "Several Days",
                  },
                  {
                    label: "More than Half the Days",
                  },
                  {
                    label: "Nearly Every Day",
                  },
                ],
              },
              {
                title:
                  "Over the past two weeks, how often have you not been able to stop or control worrying?",
                code: "GAD2-2",
                type: "radio",
                choices: [
                  {
                    label: "Not At all",
                  },
                  {
                    label: "Several Days",
                  },
                  {
                    label: "More than Half the Days",
                  },
                  {
                    label: "Nearly Every Day",
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  };
}

// -------------------------------------------------------
//
// CHANGELOG
//
// -------------------------------------------------------
// Version 2020120101
// - Initial pass
//
// -------------------------------------------------------
// Version 2022010901
// - Fix some typos
// - Reorganized sleep rule outs and parasomnia rule outs, added detailed checkboxes to each choice
// - Made wake up / sleep times a radio selection instead of freeform
// -------------------------------------------------------
// Version 2022011701
// - Make some of the questions less ambigious for the doctor reading visits by adding more context at the end of questions
//
// -------------------------------------------------------
// Version 2022012101
// - Clean up grammatical errors from Katy Suggestions
//
// -------------------------------------------------------
// Version 2022090901
// - Fix what time do you wake up on weekdays to be radio
// - Fix what time do you wake up on weekends to be radio
//
// -------------------------------------------------------
// Version 2023050501
// - Add question on what sleep aids have you tried
// - Reorder questions to put sleep stuff first
// - Add exercise to sleep hygiene question
// - Add question about how long after falling asleep do you wake up
// - Added menopausal option to conditions
// - Add question about how long does it take you to fall back asleep
// - Fix it's critical we know your full history typo
// - Added sleep app option to what have you tried
// - Added "not sure" option to chronotype
//
// -------------------------------------------------------
// Version 2023073101
// - Add question about which sleep medications you are interested in learning more about
//
// -------------------------------------------------------
// Version 2023091901
// - change 12pm to 12am on bedtime questions
//
// Version 2024010701
// - Add a new question specifically on prescription medicines that someone has tried (expand out from existing question)
// - Change the question about medicines interested in to be checkboxes
//
// Version 2024031201
// - Add detailed children for prescription medicines that someone has tried ("did it work? what dosage?")
// - Add children to interested in Gabapentin question
//
// Version 2024031201
// - Make Gabapentin and Cyclobenzaprine unselectable on "sleep medications you are interested in learning more about" in states where they are controlled or PDMP
// -------------------------------------------------------
